/*
 * Global Alert.alert shim
 * ------------------------------------------------------------------
 * Monkeypatches React Native's Alert.alert so EVERY existing `Alert.alert(...)`
 * call-site (~86 of them) renders the premium HappyAlert modal instead of the
 * plain OS dialog — with zero per-file changes.
 *
 * The success/error/warning type is inferred from the title + message text and
 * the button styles, so legacy calls still get the right animation + colour.
 *
 * Call installAlertShim() once at app start (wired in App.jsx).
 */

import { Alert } from 'react-native';
import HappyAlertController from './controller';

const SUCCESS_WORDS = ['success', 'sikeres', 'sikerült', 'kész', 'mentve', 'elmentve', 'saved', 'done', 'completed', 'köszönjük'];
const ERROR_WORDS = ['error', 'hiba', 'sikertelen', 'failed', 'nem sikerült', 'denied', 'invalid', 'érvénytelen', 'hibás'];
const WARNING_WORDS = ['warning', 'figyelem', 'figyelmeztet', 'vigyázat', 'biztos', 'are you sure', 'caution'];

const includesAny = (haystack, words) => words.some((w) => haystack.includes(w));

function inferType(title = '', message = '', buttons = []) {
    const text = `${title || ''} ${message || ''}`.toLowerCase();

    if (includesAny(text, ERROR_WORDS)) return 'error';
    if (includesAny(text, SUCCESS_WORDS)) return 'success';

    const hasDestructive = (buttons || []).some((b) => b && b.style === 'destructive');
    if (hasDestructive) return 'warning';

    if (includesAny(text, WARNING_WORDS)) return 'warning';

    // A confirm-style dialog (more than one actionable button) reads as a question.
    const actionable = (buttons || []).filter((b) => b && b.style !== 'cancel');
    if ((buttons || []).length > 1 && actionable.length >= 1) return 'question';

    return 'info';
}

export function installAlertShim() {
    if (Alert.__happyPatched) return;
    Alert.__happyNativeAlert = Alert.alert;
    Alert.__happyPatched = true;

    Alert.alert = (title, message, buttons, options) => {
        try {
            const normalized = Array.isArray(buttons) && buttons.length
                ? buttons
                : [{ text: 'OK' }];

            HappyAlertController.show({
                type: inferType(title, message, normalized),
                title: title || '',
                message: message || '',
                buttons: normalized,
                cancelable: options ? options.cancelable !== false : true,
                onDismiss: options && options.onDismiss,
            });
        } catch (_e) {
            // Never let the premium layer break a critical alert — fall back to OS.
            if (Alert.__happyNativeAlert) {
                Alert.__happyNativeAlert(title, message, buttons, options);
            }
        }
    };
}

export default installAlertShim;
